<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import scheduledMessagesAPI from 'dashboard/api/scheduledMessages';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const scheduledMessages = ref([]);
const isLoading = ref(false);

const loadScheduledMessages = async () => {
  isLoading.value = true;
  try {
    const response = await scheduledMessagesAPI.get(props.conversationId);
    scheduledMessages.value = response.data;
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const cancelMessage = async id => {
  try {
    await scheduledMessagesAPI.delete(props.conversationId, id);
    useAlert(t('SCHEDULED_MESSAGES.CANCEL_SUCCESS'));
    await loadScheduledMessages();
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.CANCEL_ERROR'));
  }
};

const formatDateTime = dateString =>
  format(new Date(dateString), 'MMM dd, yyyy HH:mm');

const getStatusClass = status => {
  const classes = {
    pending:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    sent: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    cancelled:
      'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200',
  };
  return classes[status] || classes.pending;
};

const getStatusLabel = status => {
  const labels = {
    pending: 'SCHEDULED_MESSAGES.STATUS.PENDING',
    sent: 'SCHEDULED_MESSAGES.STATUS.SENT',
    failed: 'SCHEDULED_MESSAGES.STATUS.FAILED',
    cancelled: 'SCHEDULED_MESSAGES.STATUS.CANCELLED',
  };
  return labels[status] || labels.pending;
};

onMounted(() => {
  loadScheduledMessages();
});
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header :header-title="$t('SCHEDULED_MESSAGES.LIST_TITLE')" />
    <div class="modal-content w-full pt-2 px-5 pb-6">
      <div v-if="isLoading" class="flex justify-center py-8">
        <span class="text-slate-500">
          {{ $t('SCHEDULED_MESSAGES.LOADING') }}
        </span>
      </div>

      <div v-else-if="scheduledMessages.length === 0" class="text-center py-8">
        <p class="text-slate-500">
          {{ $t('SCHEDULED_MESSAGES.NO_MESSAGES') }}
        </p>
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="message in scheduledMessages"
          :key="message.id"
          class="border border-slate-200 dark:border-slate-700 rounded-lg p-4"
        >
          <div class="flex justify-between items-start mb-2">
            <span
              class="px-2 py-1 rounded text-xs font-medium"
              :class="getStatusClass(message.status)"
            >
              {{ $t(getStatusLabel(message.status)) }}
            </span>
            <span class="text-sm text-slate-600 dark:text-slate-400">
              {{ formatDateTime(message.scheduled_at) }}
            </span>
          </div>

          <p class="text-slate-800 dark:text-slate-200 mb-3">
            {{ message.content }}
          </p>

          <div
            v-if="message.attachments && message.attachments.length > 0"
            class="mb-3 space-y-1"
          >
            <div
              v-for="attachment in message.attachments"
              :key="attachment.id"
              class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400"
            >
              <Icon icon="i-material-symbols-description" />
              <span class="truncate">{{ attachment.filename }}</span>
            </div>
          </div>

          <div v-if="message.status === 'pending'" class="flex justify-end">
            <NextButton
              faded
              slate
              size="small"
              :label="$t('SCHEDULED_MESSAGES.CANCEL_BUTTON')"
              @click="cancelMessage(message.id)"
            />
          </div>

          <div v-if="message.error_message" class="mt-2">
            <p class="text-sm text-red-600 dark:text-red-400">
              {{ message.error_message }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex justify-end mt-4">
        <NextButton
          slate
          :label="$t('SCHEDULED_MESSAGES.CLOSE')"
          @click="emit('close')"
        />
      </div>
    </div>
  </div>
</template>
